import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons"; // Import minus icon
import { Button, Input, AutoComplete } from "antd";
import { SizeType } from "antd/es/config-provider/SizeContext";
import { ChangeEvent, useState } from "react";
import styles from "./index.module.scss"; // Assuming you have a CSS module for styling

type Props = {
  name1?: string;
  name2?: string;
  label: string;
  value?: { name: string; strength: string }[];
  placeholder1?: string;
  placeholder2?: string;
  required: boolean;
  isError: boolean;
  onChange: (updatedValues: { name: string; strength: string }[]) => void;
  helperText?: string;
  prefix?: any;
  suffix?: any;
  regex?: string;
  disabled?: boolean;
  type?: string;
  size?: SizeType;
  showLabel?: boolean;
  style?: any;
  width?: any;
  rows?: number;
  isViewOnly?: boolean;
  isLogin?: boolean;
  height?: number;
  options?: { label: string; value: string }[];
  onSearch?: (value: string) => void;
};

const AddInput = (props: Props) => {
  const {
    name1,
    name2,
    label,
    value = [],
    placeholder1,
    placeholder2,
    prefix = null,
    suffix = null,
    onChange,
    disabled = false,
    type = "text",
    size = "large",
    options,
    height = 40,
    onSearch,
  } = props;
  
  const [errors, setErrors] = useState<{ name: boolean; strength: boolean }[]>([]);

  const validateFields = (index: number) => {
    const updatedErrors = [...errors];
    const fieldValue = value[index] || { name: "", strength: "" };

    updatedErrors[index] = {
      name: !fieldValue?.name?.trim(),
      strength: !fieldValue?.strength?.trim(),
    };

    setErrors(updatedErrors);
    return !updatedErrors[index].name && !updatedErrors[index].strength;
  };

  const addInputRow = () => {
    if (disabled) return;
    
    if (value?.length > 0) {
      const lastIndex = value.length - 1;
      if (!validateFields(lastIndex)) return;
    }

    setErrors([...errors, { name: false, strength: false }]); // Reset errors for the new row
    onChange([...value, { name: "", strength: "" }]);
  };

  const handleRemoveRow = (index: number) => {
    if (disabled) return;
    
    const updatedValues = value.filter((_, i) => i !== index);
    onChange(updatedValues);

    const updatedErrors = errors.filter((_, i) => i !== index);
    setErrors(updatedErrors);
  };

  const handleChange = (index: number, field: "name" | "strength", inputValue: string) => {
    if (disabled) return;
    
    let sanitizedValue = inputValue.replace(/^[-@$+#]+/, "");
    const updatedValues = [...value];
    updatedValues[index] = { ...updatedValues[index], [field]: sanitizedValue };
    onChange(updatedValues);

    // Remove error message when user fills the input
    const updatedErrors = [...errors];
    const isEmptyString = sanitizedValue === "";
    updatedErrors[index] = {
      ...(updatedErrors[index] || {}),
      [field]: isEmptyString ? false : !sanitizedValue.trim(),
    };
    setErrors(updatedErrors);
  };

  return (
    <div className={styles["add-input"]}>
      {(value.length === 0 ? [{ name: "", strength: "" }] : value).map((val, index) => (
        <div key={index} className="gap-1">

          <div className={styles["add-input__label"]}>
            {index === 0 && (
              <p className="mb-1 text-gray-700 font-semibold">{label}</p>
            )}
            {/* {index === 0 && ( */}
              {/* <Button
                type="primary"
                shape="circle"
                icon={<PlusOutlined style={{ fontSize: 10 }} />}
                onClick={addInputRow}
                size="small"
                disabled={disabled}
              /> */}
            {/* )} */}
          </div>

          <div className={styles["add-input__input"]}>
            <div className={styles["add-input__field"]}>
              <AutoComplete
                options={options}
                onSearch={onSearch}
                value={val?.name || ""}
                onChange={(autoValue) => handleChange(index, "name", String(autoValue))}
                disabled={disabled}
              >
                <Input
                  name={name1}
                  placeholder={placeholder1}
                  prefix={prefix}
                  suffix={suffix}
                  type={type}
                  height={height}
                  size={size}
                  disabled={disabled}
                />
              </AutoComplete>
              <div className={styles["add-input__error-wrap"]}>
                {errors[index]?.name && (
                  <p className={styles["add-input__error"]}>Please fill the input.</p>
                )}
              </div>
            </div>
            <div className={styles["add-input__field"]}>
              <Input
                name={name2}
                placeholder={placeholder2}
                prefix={prefix}
                suffix={suffix}
                size={size}
                height={height}
                value={val?.strength || ""}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleChange(index, "strength", e.target.value)
                }
                type={type}
                disabled={disabled}
              />
              <div className={styles["add-input__error-wrap"]}>
                {errors[index]?.strength && (
                  <p className={styles["add-input__error"]}>Please fill the input.</p>
                )}
              </div>
            </div>
            
            {value.length > 1 && (
              <Button
                type="default"
                shape="circle"
                icon={<MinusCircleOutlined style={{ fontSize: 12, color: "red" }} />}
                onClick={() => handleRemoveRow(index)}
                size="small"
                disabled={disabled}
              />
            )}
            {index === (value.length || 1) - 1 && (
              <Button
                type="primary"
                shape="circle"
                icon={<PlusOutlined style={{ fontSize: 10 }} />}
                onClick={addInputRow}
                size="small"
                disabled={disabled}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default AddInput;
